import { createBrowserRouter, Navigate, Outlet } from 'react-router'
import { useTranslation } from 'react-i18next'
import { RequireAuth, RequireRole } from '@/auth/guards'
import { ReAuthProvider } from '@/auth/ReAuthProvider'
import { PLATFORM_ROLES, TENANT_PORTAL_ROLES } from '@/auth/roles'
import { PublicLayout } from '@/layouts/PublicLayout'
import { LandingPage } from '@/features/public-site/pages/LandingPage'
import { LoginPage } from '@/features/auth/pages/LoginPage'
import { ComingSoon, ForbiddenPage, NotFoundPage, RootErrorBoundary } from '@/features/system/pages'
import { DASHBOARD_NAV } from '@/layouts/nav'

/**
 * Router assembly. Each feature family will export its own route module as it
 * is built (Phases 1–10); placeholders keep every sidebar destination live.
 */

function RootLayout() {
  return (
    <ReAuthProvider>
      <Outlet />
    </ReAuthProvider>
  )
}

function NavComingSoon({ labelKey }: { labelKey: string }) {
  const { t } = useTranslation()
  return <ComingSoon title={t(`nav.${labelKey}`)} />
}

/** Routes with real implementations — excluded from the placeholder sweep. */
const IMPLEMENTED_DASHBOARD_PATHS = new Set([
  '/dashboard',
  '/dashboard/products',
  '/dashboard/plans',
  '/dashboard/users',
  '/dashboard/invites',
  '/dashboard/subscriptions',
  '/dashboard/billing',
  '/dashboard/organisations',
  '/dashboard/team',
  '/dashboard/audit',
  '/dashboard/auth',
  '/dashboard/api-keys',
  '/dashboard/branding',
])

/** Placeholder children generated from the nav config so no link 404s. */
const dashboardPlaceholders = DASHBOARD_NAV.flatMap((section) => section.items)
  .filter((item) => !IMPLEMENTED_DASHBOARD_PATHS.has(item.to))
  .map((item) => ({
    path: item.to.replace('/dashboard/', ''),
    element: <NavComingSoon labelKey={item.labelKey} />,
  }))

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { index: true, element: <LandingPage /> },
          {
            path: 'pricing',
            lazy: async () => ({
              Component: (await import('@/features/public-site/pages/PricingPage')).PricingPage,
            }),
          },
        ],
      },
      { path: 'login', element: <LoginPage /> },
      {
        path: 'signup',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/SignupPage')).SignupPage,
        }),
      },
      {
        path: 'signup/verify',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/VerifyEmailPages')).VerifyEmailHoldPage,
        }),
      },
      {
        path: 'signup/verified',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/VerifyEmailPages')).VerifyEmailPage,
        }),
      },
      {
        path: 'auth/mfa',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/MfaChallengePage')).MfaChallengePage,
        }),
      },
      {
        path: 'forgot-password',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/PasswordRecoveryPages'))
            .ForgotPasswordPage,
        }),
      },
      {
        path: 'reset-password',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/PasswordRecoveryPages'))
            .ResetPasswordPage,
        }),
      },
      {
        path: 'invite/accept',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/AcceptInvitePage')).AcceptInvitePage,
        }),
      },
      {
        path: 'account/pending-approval',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/StatePages')).ApprovalPendingPage,
        }),
      },
      {
        path: 'account/suspended',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/StatePages')).SuspendedPage,
        }),
      },
      {
        path: 'session-expired',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/StatePages')).SessionExpiredPage,
        }),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            path: 'onboarding',
            lazy: async () => ({
              Component: (await import('@/features/onboarding/OnboardingLayout')).OnboardingLayout,
            }),
            children: [
              {
                path: 'plan',
                lazy: async () => ({
                  Component: (await import('@/features/onboarding/pages/PlanStep')).PlanStep,
                }),
              },
              {
                path: 'payment',
                lazy: async () => ({
                  Component: (await import('@/features/onboarding/pages/PaymentStep')).PaymentStep,
                }),
              },
              {
                path: 'product',
                lazy: async () => ({
                  Component: (await import('@/features/onboarding/pages/ProductStep')).ProductStep,
                }),
              },
              {
                path: 'launch',
                lazy: async () => ({
                  Component: (await import('@/features/onboarding/pages/LaunchStep')).LaunchStep,
                }),
              },
            ],
          },
          {
            element: <RequireRole roles={[...TENANT_PORTAL_ROLES]} />,
            children: [
              {
                path: 'dashboard',
                lazy: async () => ({
                  Component: (await import('@/layouts/DashboardShell')).DashboardShell,
                }),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import('@/features/dashboard/pages/DashboardHomePage'))
                        .DashboardHomePage,
                    }),
                  },
                  {
                    path: 'settings',
                    lazy: async () => ({
                      Component: (await import('@/features/settings/SettingsLayout'))
                        .SettingsLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/settings/EmailTab')).EmailTab,
                        }),
                      },
                      {
                        path: 'compliance',
                        lazy: async () => ({
                          Component: (await import('@/features/settings/ComplianceTab'))
                            .ComplianceTab,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'auth',
                    lazy: async () => ({
                      Component: (await import('@/features/identity/AuthLayout')).AuthLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/identity/SsoPage')).SsoPage,
                        }),
                      },
                      {
                        path: 'security',
                        lazy: async () => ({
                          Component: (await import('@/features/identity/SecurityPolicyPage'))
                            .SecurityPolicyPage,
                        }),
                      },
                      {
                        path: 'scim',
                        lazy: async () => ({
                          Component: (await import('@/features/identity/ScimPage')).ScimPage,
                        }),
                      },
                      {
                        path: 'idp/:idpId/mappers',
                        lazy: async () => ({
                          Component: (await import('@/features/identity/MappersPage')).MappersPage,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'api-keys',
                    lazy: async () => ({
                      Component: (await import('@/features/integrations/DevLayout')).DevLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/integrations/ApiKeysPage'))
                            .ApiKeysPage,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'webhooks',
                    lazy: async () => ({
                      Component: (await import('@/features/integrations/DevLayout')).DevLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/integrations/WebhooksPage'))
                            .WebhooksPage,
                        }),
                      },
                      {
                        path: ':webhookId',
                        lazy: async () => ({
                          Component: (await import('@/features/integrations/WebhookDetailPage'))
                            .WebhookDetailPage,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'branding',
                    lazy: async () => ({
                      Component: (await import('@/features/branding/BrandingSettingsLayout'))
                        .BrandingSettingsLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/branding/BrandingPage'))
                            .BrandingPage,
                        }),
                      },
                      {
                        path: 'domains',
                        lazy: async () => ({
                          Component: (await import('@/features/branding/DomainsPage')).DomainsPage,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'my-billing',
                    lazy: async () => ({
                      Component: (await import('@/features/my-billing/MyBillingPage'))
                        .MyBillingPage,
                    }),
                  },
                  {
                    path: 'products',
                    lazy: async () => ({
                      Component: (await import('@/features/products/ProductsPage')).ProductsPage,
                    }),
                  },
                  {
                    path: 'plans',
                    lazy: async () => ({
                      Component: (await import('@/features/plans/PlansPage')).PlansPage,
                    }),
                  },
                  {
                    path: 'plans/rules',
                    lazy: async () => ({
                      Component: (await import('@/features/plans/UpgradeRulesPage'))
                        .UpgradeRulesPage,
                    }),
                  },
                  {
                    path: 'plans/:slug',
                    lazy: async () => ({
                      Component: (await import('@/features/plans/PlanDetailPage')).PlanDetailPage,
                    }),
                  },
                  {
                    path: 'users',
                    lazy: async () => ({
                      Component: (await import('@/features/users/UsersPage')).UsersPage,
                    }),
                    children: [
                      {
                        path: ':userId',
                        lazy: async () => ({
                          Component: (await import('@/features/users/UserDrawer')).UserDrawer,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'invites',
                    lazy: async () => ({
                      Component: (await import('@/features/invites/InvitesPage')).InvitesPage,
                    }),
                  },
                  {
                    path: 'subscriptions',
                    lazy: async () => ({
                      Component: (await import('@/features/subscriptions/SubscriptionsPage'))
                        .SubscriptionsPage,
                    }),
                  },
                  {
                    path: 'subscriptions/:subId',
                    lazy: async () => ({
                      Component: (await import('@/features/subscriptions/SubscriptionDetailPage'))
                        .SubscriptionDetailPage,
                    }),
                  },
                  {
                    path: 'billing',
                    lazy: async () => ({
                      Component: (await import('@/features/billing/BillingLayout')).BillingLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/billing/RevenueTab')).RevenueTab,
                        }),
                      },
                      {
                        path: 'invoices',
                        lazy: async () => ({
                          Component: (await import('@/features/billing/InvoicesTab')).InvoicesTab,
                        }),
                      },
                      {
                        path: 'refunds',
                        lazy: async () => ({
                          Component: (await import('@/features/billing/RefundsTab')).RefundsTab,
                        }),
                      },
                      {
                        path: 'disputes',
                        lazy: async () => ({
                          Component: (await import('@/features/billing/DisputesTab')).DisputesTab,
                        }),
                      },
                      {
                        path: 'disputes/:disputeId',
                        lazy: async () => ({
                          Component: (await import('@/features/billing/DisputeDetailPage'))
                            .DisputeDetailPage,
                        }),
                      },
                      {
                        path: 'events',
                        lazy: async () => ({
                          Component: (await import('@/features/billing/EventsTab')).EventsTab,
                        }),
                      },
                    ],
                  },
                  // Mockup 12a addresses the queue as /dashboard/refunds.
                  {
                    path: 'refunds',
                    element: <Navigate to="/dashboard/billing/refunds" replace />,
                  },
                  {
                    path: 'organisations',
                    lazy: async () => ({
                      Component: (await import('@/features/organisations/OrganisationsPage'))
                        .OrganisationsPage,
                    }),
                  },
                  {
                    path: 'organisations/:orgId',
                    lazy: async () => ({
                      Component: (await import('@/features/organisations/OrganisationDetailPage'))
                        .OrganisationDetailPage,
                    }),
                  },
                  {
                    path: 'team',
                    lazy: async () => ({
                      Component: (await import('@/features/team/TeamPage')).TeamPage,
                    }),
                  },
                  {
                    path: 'audit',
                    lazy: async () => ({
                      Component: (await import('@/features/audit/AuditLogPage')).AuditLogPage,
                    }),
                  },
                  ...dashboardPlaceholders,
                ],
              },
            ],
          },
          {
            element: <RequireRole roles={[...PLATFORM_ROLES]} />,
            children: [
              {
                path: 'admin',
                lazy: async () => ({
                  Component: (await import('@/layouts/PlatformShell')).PlatformShell,
                }),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import('@/features/platform/ConsoleHomePage'))
                        .ConsoleHomePage,
                    }),
                  },
                  {
                    path: 'tenants',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/TenantDirectoryPage'))
                        .TenantDirectoryPage,
                    }),
                  },
                  {
                    path: 'tenants/:tenantId',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/TenantDetailPage'))
                        .TenantDetailPage,
                    }),
                  },
                  {
                    path: 'metrics',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/MetricsPage')).MetricsPage,
                    }),
                  },
                  {
                    path: 'outbox',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/OutboxPage')).OutboxPage,
                    }),
                  },
                  {
                    path: 'fraud',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/FraudPage')).FraudPage,
                    }),
                  },
                  {
                    path: 'plans',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/PlatformPlansPage'))
                        .PlatformPlansPage,
                    }),
                  },
                  {
                    path: 'compliance',
                    lazy: async () => ({
                      Component: (await import('@/features/platform/CompliancePage'))
                        .CompliancePage,
                    }),
                  },
                ],
              },
            ],
          },
          {
            path: 'portal/:tenantSlug',
            lazy: async () => ({
              Component: (await import('@/layouts/PortalShell')).PortalShell,
            }),
            children: [
              { index: true, element: <Navigate to="account" replace /> },
              { path: 'dashboard', element: <Navigate to="../account" replace /> },
              {
                path: 'account',
                lazy: async () => ({
                  Component: (await import('@/features/portal/PortalAccountPage'))
                    .PortalAccountPage,
                }),
              },
              {
                path: 'billing',
                lazy: async () => ({
                  Component: (await import('@/features/portal/PortalBillingPage'))
                    .PortalBillingPage,
                }),
              },
              {
                path: 'team',
                lazy: async () => ({
                  Component: (await import('@/features/portal/PortalTeamPage')).PortalTeamPage,
                }),
              },
              {
                path: 'security',
                lazy: async () => ({
                  Component: (await import('@/features/portal/PortalSecurityPage'))
                    .PortalSecurityPage,
                }),
              },
            ],
          },
        ],
      },
      // End-user product registration is public — no session yet (11a).
      {
        path: 'portal/:tenantSlug/register',
        lazy: async () => ({
          Component: (await import('@/features/portal/PortalRegisterPage')).PortalRegisterPage,
        }),
      },
      { path: 'forbidden', element: <ForbiddenPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
